import {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Link, useSearchParams} from "react-router-dom";
import {paths} from "@/lib/paths";
import {
  ApiError,
  resetCart,
  getCart,
  removeCartItem,
  updateCartItem,
  addCartVoucher,
  removeCartVoucher,
  formatMoneyDisplay,
  productImageFromUrl,
  stripVoucherQueryParams,
  voucherCodeFromSearchParams,
  type CartDetailsResponse,
  type CartLine,
  type I18nKey
} from "@zaaaz/shared";
import {DsButton, DsEmptyState, DsInput} from "@zaaaz/ui";
import {useI18n} from "./I18nProvider";
import {ProductPrice} from "./ProductPrice";
import {MoneyDisplay} from "./MoneyDisplay";

type VoucherNotice = {
  variant: "ok" | "warn" | "err";
  title: I18nKey;
  message: string;
};

function CartVoucherNotice({notice, onClose, t}: {notice: VoucherNotice | null; onClose: () => void; t: (k: I18nKey) => string}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    if(!notice) return;
    function onKey(e: KeyboardEvent) {
      if(e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [notice, onClose]);
  if(!mounted || !notice) return null;
  const icon = notice.variant === "ok" ? "fa-check" : notice.variant === "warn" ? "fa-exclamation-triangle" : "fa-times-circle";
  return createPortal(
    <div className="cc-cartVoucherDialog" role="alertdialog" aria-modal="true" aria-labelledby="cc-cartVoucherDialogTitle" aria-describedby="cc-cartVoucherDialogMsg">
      <button type="button" className="cc-cartVoucherDialog__backdrop anim" aria-label={t("Close")} onClick={onClose} />
      <div className={`cc-cartVoucherDialog__panel cc-cartVoucherDialog__panel--${notice.variant}`}>
        <span className={`cc-cartVoucherDialog__icon cc-cartVoucherDialog__icon--${notice.variant}`} aria-hidden="true">
          <i className={`fa ${icon}`} />
        </span>
        <h2 id="cc-cartVoucherDialogTitle" className="cc-cartVoucherDialog__title">{t(notice.title)}</h2>
        <p id="cc-cartVoucherDialogMsg" className="cc-cartVoucherDialog__message">{notice.message}</p>
        <DsButton type="button" variant="primary" size="md" fullWidth className="cc-cartVoucherDialog__btn anim" onClick={onClose}>
          {t("Close")}
        </DsButton>
      </div>
    </div>,
    document.body
  );
}

function CartLineControls({
  line,
  onQty,
  onRemove,
  t
}: {
  line: CartLine;
  onQty: (warehouse: string, productId: number, qty: number) => void;
  onRemove: (warehouse: string, productId: number) => void;
  t: (k: I18nKey) => string;
}) {
  const [draftQty, setDraftQty] = useState(String(line.quantity));

  useEffect(() => {
    setDraftQty(String(line.quantity));
  }, [line.quantity]);

  function onDraftChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 3);
    setDraftQty(digits);
  }

  function commitDraft() {
    const parsed = parseInt(draftQty, 10);
    if(!draftQty || Number.isNaN(parsed) || parsed < 1) {
      setDraftQty(String(line.quantity));
      return;
    }
    const next = Math.min(999, parsed);
    setDraftQty(String(next));
    if(next !== line.quantity) onQty(line.warehouse, line.productId, next);
  }

  return (
    <div className="cc-cart__controls">
      <button type="button" className="cc-cart__qtyBtn" aria-label="-" onClick={() => onQty(line.warehouse, line.productId, line.quantity - 1)}>
        <i className="fa fa-minus" aria-hidden="true" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={3}
        className="cc-cart__qtyInput"
        value={draftQty}
        aria-label={t("Qty")}
        onChange={(e) => onDraftChange(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={(e) => { if(e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
      />
      <button type="button" className="cc-cart__qtyBtn cc-cart__qtyBtn--plus" aria-label="+" onClick={() => onQty(line.warehouse, line.productId, line.quantity + 1)}>
        <i className="fa fa-plus" aria-hidden="true" />
      </button>
      <button type="button" className="cc-cart__removeBtn anim" aria-label={t("Remove")} onClick={() => onRemove(line.warehouse, line.productId)}>
        <i className="fa fa-trash-o" aria-hidden="true" />
      </button>
    </div>
  );
}

function CartSummary({cart, t}: {cart: CartDetailsResponse; t: (k: I18nKey) => string}) {
  const {currency} = useI18n();
  const rate = cart.eurRate || 4.97;
  const hasDiscount = cart.discount > 0;
  const displaySub = hasDiscount ? cart.totalAfterDiscount : cart.subtotal;
  const total = cart.shippingCost === null ? displaySub : cart.grandTotal;
  const shipProgress = Math.min(100, Math.round((cart.subtotal / 100) * 100));
  const shipRemain = formatMoneyDisplay(cart.shippingRemainingLei, currency, rate);
  return (
    <>
      <div className="cc-cart__rows">
        {hasDiscount ? (
          <>
            <div className="cc-cart__row"><span>{t("Discount")}</span><b>-<MoneyDisplay lei={cart.discount} eurRate={rate} /></b></div>
            <div className="cc-cart__row"><span>{t("Subtotal")}</span><b><MoneyDisplay lei={cart.totalAfterDiscount} eurRate={rate} /></b></div>
          </>
        ) : (
          <div className="cc-cart__row"><span>{t("Subtotal")}</span><b><MoneyDisplay lei={cart.subtotal} eurRate={rate} /></b></div>
        )}
        <div className="cc-cart__row">
          <span>{t("Shipping")}</span>
          <b>{cart.freeDelivery ? t("Free") : (cart.shippingCost === null ? "-" : <MoneyDisplay lei={cart.shippingCost} eurRate={rate} />)}</b>
        </div>
        <div className="cc-cart__row cc-cart__row--total"><span>{t("Total")}</span><b><MoneyDisplay lei={total} eurRate={rate} /></b></div>
      </div>
      {cart.freeDelivery ? (
        <p className="cc-cart__shipHint cc-cart__shipHint--free" dangerouslySetInnerHTML={{__html: t("Congratulations, you get FREE DELIVERY for this order!")}} />
      ) : (
        <div className="cc-cart__shipHint">
          <span>{t("Add another")} {shipRemain} {t("to your shopping bag to get FREE DELIVERY!")}</span>
          <div className="cc-cart__shipBar" aria-hidden="true"><div className="cc-cart__shipBarFill" style={{width: `${shipProgress}%`}} /></div>
        </div>
      )}
      <Link to={cart.checkoutUrl || paths.checkout} className="cc-cart__checkoutLink anim">
        <DsButton variant="primary" size="md" fullWidth>
          {t("Checkout")} <i className="fa fa-long-arrow-right" aria-hidden="true" />
        </DsButton>
      </Link>
    </>
  );
}

export function CartView() {
  const {t} = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoVoucherDone = useRef("");
  const [cart, setCart] = useState<CartDetailsResponse | null>(null);
  const [error, setError] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherNotice, setVoucherNotice] = useState<VoucherNotice | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fromUrl = voucherCodeFromSearchParams(searchParams);
      if(fromUrl && autoVoucherDone.current !== fromUrl) {
        autoVoucherDone.current = fromUrl;
        try {
          const r = await addCartVoucher(fromUrl);
          if(cancelled) return;
          setCart(r);
          setVoucherNotice(
            r.voucherWarning
              ? {variant: "warn", title: "Voucher", message: t("Voucher not applicable")}
              : {variant: "ok", title: "Voucher", message: t("Voucher applied successfully")}
          );
        } catch(e) {
          if(cancelled) return;
          setVoucherCode(fromUrl);
          setVoucherNotice({
            variant: "err",
            title: "Voucher",
            message: e instanceof ApiError && e.body?.code === "SELF_VOUCHER"
              ? t("You cannot use your own influencer voucher")
              : `${t("Invalid voucher code")}.`
          });
          try {
            const r = await getCart();
            if(!cancelled) setCart(r);
          } catch(err) {
            if(!cancelled) setError(err instanceof Error ? err.message : t("Cart update failed"));
          }
        }
        setSearchParams(stripVoucherQueryParams(searchParams), {replace: true});
        return;
      }
      try {
        const r = await getCart();
        if(!cancelled) setCart(r);
      } catch(e) {
        if(!cancelled) setError(e instanceof Error ? e.message : t("Cart update failed"));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function onRemove(warehouse: string, productId: number) {
    try {
      const r = await removeCartItem(warehouse, productId);
      setCart(r);
      setError("");
      window.dispatchEvent(new CustomEvent("cart-updated", {detail: r.count}));
    } catch(e) {
      setError(e instanceof Error ? e.message : t("Cart update failed"));
    }
  }
  async function onQty(warehouse: string, productId: number, qty: number) {
    const next = Math.max(1, qty);
    try {
      const r = await updateCartItem(warehouse, productId, next);
      setCart(r);
      setError("");
      window.dispatchEvent(new CustomEvent("cart-updated", {detail: r.count}));
    } catch(e) {
      setError(e instanceof Error ? e.message : t("Cart update failed"));
    }
  }
  async function onClear() {
    try {
      const r = await resetCart();
      setCart(r);
      setError("");
      window.dispatchEvent(new CustomEvent("cart-updated", {detail: 0}));
    } catch(e) {
      setError(e instanceof Error ? e.message : t("Cart update failed"));
    }
  }
  async function onVoucher(e: React.FormEvent) {
    e.preventDefault();
    if(!voucherCode.trim()) return;
    try {
      const r = await addCartVoucher(voucherCode.trim());
      setCart(r);
      setVoucherCode("");
      setVoucherNotice(
        r.voucherWarning
          ? {variant: "warn", title: "Voucher", message: t("Voucher not applicable")}
          : {variant: "ok", title: "Voucher", message: t("Voucher applied successfully")}
      );
    } catch(err) {
      setVoucherNotice({
        variant: "err",
        title: "Voucher",
        message: err instanceof ApiError && err.body?.code === "SELF_VOUCHER"
          ? t("You cannot use your own influencer voucher")
          : `${t("Invalid voucher code")}.`
      });
    }
  }
  async function onRemoveVoucher(id: number) {
    try {
      const r = await removeCartVoucher(id);
      setCart(r);
      setVoucherNotice(null);
    } catch(e) {
      setError(e instanceof Error ? e.message : t("Cart update failed"));
    }
  }
  if(!cart && !error) {
    return (
      <div className="cc-cart">
        <div className="cc-cart__loading">
          <div className="cc-cart__spinner" aria-hidden="true" />
          <span>{t("Loading products...")}</span>
        </div>
      </div>
    );
  }
  if(error && !cart) return <div className="cc-cart"><p className="cc-cart__alert cc-cart__alert--err">{error}</p></div>;
  if(!cart || !cart.lines.length) {
    return (
      <div className="cc-cart">
        <div className="cc-cart__empty">
          <DsEmptyState
            icon={<i className="fa fa-shopping-bag" aria-hidden="true" />}
            title={t("Your cart is empty")}
          />
        </div>
      </div>
    );
  }
  const itemCount = cart.lines.reduce((n, l) => n + l.quantity, 0);
  return (
    <div className="cc-cart">
      <CartVoucherNotice notice={voucherNotice} onClose={() => setVoucherNotice(null)} t={t} />
      <header className="cc-cart__hero">
        <div className="cc-cart__heroInner">
          <div className="cc-cart__heroCopy">
            <h1 className="cc-cart__title">{t("My cart")}</h1>
            <p className="cc-cart__count">{itemCount} {t("products")}</p>
          </div>
          <DsButton type="button" variant="outline" size="sm" className="cc-cart__clear anim" onClick={onClear}>
            <i className="fa fa-trash-o" aria-hidden="true" /> {t("Remove all products")}
          </DsButton>
        </div>
      </header>
      {error ? <p className="cc-cart__alert cc-cart__alert--err">{error}</p> : null}
      {cart.voucherWarning ? <p className="cc-cart__alert cc-cart__alert--warn">{t("Voucher not applicable")}</p> : null}
      <div className="cc-cart__layout">
        <div className="cc-cart__list">
          {cart.lines.map((line) => (
            <article key={`${line.warehouse}-${line.productId}`} className="cc-cart__line">
              <Link to={line.product.url} className="cc-cart__thumb anim">
                <img src={productImageFromUrl(line.product.imagine)} alt={line.product.nume} />
              </Link>
              <div className="cc-cart__info">
                <span className="cc-cart__brand">{line.product.brand}</span>
                <Link to={line.product.url} className="cc-cart__name anim">{line.product.nume}</Link>
              </div>
              <div className="cc-cart__meta">
                <div className="cc-cart__linePrice">
                  <ProductPrice pretEuro={line.pretEuro || line.product.pretEuro} pretLei={line.pretLei} />
                </div>
                <CartLineControls line={line} onQty={onQty} onRemove={onRemove} t={t} />
              </div>
            </article>
          ))}
          {cart.vouchers.map((v) => (
            <div key={v.id} className="cc-cart__voucherRow">
              <span className="cc-cart__voucherIcon"><i className="fa fa-tag" /></span>
              <div className="cc-cart__voucherBody">
                <p className="cc-cart__voucherTitle">{t("Voucher")}: {v.nume}</p>
                <p className="cc-cart__voucherMeta">{v.label}{v.hint ? ` · ${v.hint}` : ""}</p>
              </div>
              <button type="button" className="cc-cart__voucherRemove anim" onClick={() => onRemoveVoucher(v.id)}><i className="fa fa-times" /></button>
            </div>
          ))}
        </div>
        <aside className="cc-cart__aside">
          <div className="cc-cart__panel">
            <h2 className="cc-cart__panelTitle">{t("Cart total")}</h2>
            <form className="cc-cart__voucherForm" onSubmit={onVoucher}>
              <DsInput
                className="cc-cart__voucherInput"
                placeholder={t("Enter the promotional code")}
                name="voucher"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value)}
              />
              <DsButton type="submit" variant="primary" size="sm" className="cc-cart__voucherApply" title={t("Use voucher")}>
                <i className="fa fa-check" aria-hidden="true" />
              </DsButton>
            </form>
            <CartSummary cart={cart} t={t} />
          </div>
        </aside>
      </div>
    </div>
  );
}
